// src/components/admin/mail/MailMessageDetail.tsx
import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, Forward, ImageOff, Mail, Reply, ReplyAll, Trash2 } from 'lucide-react';
import { LoadStatus, MailFolderId, MailMessageDetail as MailMessageDetailType } from '../../../types/mail';
import MailAttachmentList from './MailAttachmentList';
import MailBodyFrame from './MailBodyFrame';
import MailEmptyState from './MailEmptyState';
import { formatAddress, formatAddressList, formatFullDate } from './mailUtils';
import { BORDER, Button, MUTED, PaneHeader, SURFACE } from './mail.styles';

const HeaderBlock = styled.div`
    padding: 1.25rem;
    border-bottom: 1px solid ${BORDER};
    box-sizing: border-box;
`;

const Subject = styled.h3`
    margin: 0 0 0.65rem 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.35;
    word-break: break-word;
`;

const MetaRow = styled.div`
    display: flex;
    gap: 0.5rem;
    font-size: 0.83rem;
    color: ${MUTED};
    margin-top: 0.2rem;
    flex-wrap: wrap;
`;

const MetaLabel = styled.span`
    color: #94a3b8;
    flex-shrink: 0;
`;

const MetaValue = styled.span`
    color: #475569;
    word-break: break-word;
    min-width: 0;
`;

const Actions = styled.div`
    display: flex;
    gap: 0.6rem;
    padding: 0.85rem 1.25rem;
    border-bottom: 1px solid ${BORDER};
    background: ${SURFACE};
    flex-wrap: wrap;
    box-sizing: border-box;
`;

const Notice = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: 0.85rem 1.25rem 0;
    padding: 0.7rem 0.9rem;
    background: #f8fafc;
    border-left: 3px solid #f59e0b;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #64748b;
    flex-wrap: wrap;
    box-sizing: border-box;
`;

const LinkButton = styled.button`
    border: none;
    background: none;
    color: #990147;
    font-weight: 600;
    font-size: 0.82rem;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:focus-visible {
        outline: 2px solid rgba(153, 1, 71, 0.4);
        outline-offset: 2px;
    }
`;

const BodyArea = styled.div`
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

interface Props {
    accountId: string;
    folder: MailFolderId;
    detail: MailMessageDetailType | null;
    detailStatus: LoadStatus;
    imagesAllowed: boolean;
    onReply: () => void;
    onReplyAll: () => void;
    onForward: () => void;
    onDelete: () => void;
    onMarkUnread: () => void;
    onShowImages: () => void;
    onRetry: () => void;
    onBack?: () => void;
}

const MailMessageDetail: React.FC<Props> = ({
    accountId,
    folder,
    detail,
    detailStatus,
    imagesAllowed,
    onReply,
    onReplyAll,
    onForward,
    onDelete,
    onMarkUnread,
    onShowImages,
    onRetry,
    onBack,
}) => {
    if (detailStatus === 'idle') return <MailEmptyState variant="noSelection" />;
    if (detailStatus === 'loading') return <MailEmptyState variant="detailLoading" />;
    if (detailStatus === 'error' || !detail) {
        return <MailEmptyState variant="detailError" onRetry={onRetry} />;
    }

    return (
        <>
            {onBack && (
                <PaneHeader>
                    <Button type="button" $variant="secondary" onClick={onBack} whileTap={{ scale: 0.97 }}>
                        <ChevronLeft size={16} />
                        Späť na zoznam
                    </Button>
                </PaneHeader>
            )}

            <HeaderBlock>
                <Subject>{detail.subject}</Subject>
                <MetaRow>
                    <MetaLabel>Od:</MetaLabel>
                    <MetaValue>
                        {formatAddress(detail.from)}
                        {detail.from && detail.from.name ? ` <${detail.from.address}>` : ''}
                    </MetaValue>
                </MetaRow>
                {detail.to.length > 0 && (
                    <MetaRow>
                        <MetaLabel>Komu:</MetaLabel>
                        <MetaValue>{formatAddressList(detail.to)}</MetaValue>
                    </MetaRow>
                )}
                {detail.cc.length > 0 && (
                    <MetaRow>
                        <MetaLabel>Kópia:</MetaLabel>
                        <MetaValue>{formatAddressList(detail.cc)}</MetaValue>
                    </MetaRow>
                )}
                <MetaRow>
                    <MetaLabel>Dátum:</MetaLabel>
                    <MetaValue>{formatFullDate(detail.sentAt)}</MetaValue>
                </MetaRow>
            </HeaderBlock>

            <Actions>
                <Button type="button" onClick={onReply} whileTap={{ scale: 0.97 }}>
                    <Reply size={16} />
                    Odpovedať
                </Button>
                {(detail.to.length + detail.cc.length) > 1 && (
                    <Button type="button" $variant="secondary" onClick={onReplyAll} whileTap={{ scale: 0.97 }}>
                        <ReplyAll size={16} />
                        Odpovedať všetkým
                    </Button>
                )}
                <Button type="button" $variant="secondary" onClick={onForward} whileTap={{ scale: 0.97 }}>
                    <Forward size={16} />
                    Preposlať
                </Button>
                <Button type="button" $variant="secondary" onClick={onMarkUnread} whileTap={{ scale: 0.97 }}>
                    <Mail size={16} />
                    Neprečítané
                </Button>
                <Button type="button" $variant="danger" onClick={onDelete} whileTap={{ scale: 0.97 }}>
                    <Trash2 size={16} />
                    {folder === 'trash' ? 'Zmazať natrvalo' : 'Zmazať'}
                </Button>
            </Actions>

            {detail.remoteImagesBlocked && (
                <Notice>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                        <ImageOff size={15} />
                        Obrázky boli zablokované kvôli ochrane súkromia.
                    </span>
                    <LinkButton type="button" onClick={onShowImages}>
                        Zobraziť obrázky
                    </LinkButton>
                </Notice>
            )}

            {detail.truncated && (
                <Notice>
                    <span>Správa je veľmi dlhá a bola skrátená.</span>
                </Notice>
            )}

            <BodyArea>
                <MailBodyFrame
                    html={detail.bodyHtml}
                    text={detail.bodyText}
                    allowRemoteImages={imagesAllowed}
                />
            </BodyArea>

            <MailAttachmentList
                accountId={accountId}
                folder={folder}
                uid={detail.uid}
                uidValidity={detail.uidValidity}
                attachments={detail.attachments}
            />
        </>
    );
};

export default MailMessageDetail;
